import {groupByUniqueKey} from '@benchling/js-utils';

import * as analytics from 'analytics/Analytics';
import {Sequence} from 'components/Sequence';
import {ANALYTICS_MODULE} from 'constants/AnalyticsConstants';
import {
  LINEAR_LAYER_FULL,
  LINEAR_LAYER_INLINE,
  LINEAR_LAYER_INLINE_PDF,
  MAX_VISIBLE_RING_FEATURES,
} from 'constants/MapsConstants';
import {Strand} from 'constants/MolbioConstants';
import {SegmentsRingData} from 'molbio/maps/circular/CircularTypedefs';
import {PolymerAnnotationsViewData} from 'molbio/maps/linear/PolymerAnnotationsView';
import {RegionSelectable} from 'molbio/maps/selectables/Selectables';
import {isRangeOverlap} from 'molbio/SeqUtil';
import Translation from 'molbio/translations/backbone/Translation';
import {
  getTranslationRegionFormattedName,
  getTranslationRegionGroupsForSequence,
  TranslationRegionData,
  TranslationRegionGroupData,
} from 'molbio/translations/TranslationUtil';

/**
 * Get a TranslationAsAnnotation model for every translation in the give range.
 *
 * This is intended for use on the linear view and plasmid map and is a convenient
 * way to efficiently access all translations. This function always considers the
 * full list of translations even if the range is small, so it should only be used
 * in cases where it will be called once per render rather than many times per render.
 */
export function getTranslationsAsAnnotationsInRange(
  sequence: Sequence,
  start: number,
  end: number,
  showAll: boolean = false
): {
  numTotal: number;
  numVisible: number;
  numShown: number;
  shownTranslations: Array<TranslationAsAnnotation>;
} {
  const translationGroupData = getTranslationRegionGroupsForSequence(sequence);
  const translationComponentModelsByCid = groupByUniqueKey(
    sequence.translations().models,
    (translation) => translation.cid
  );
  let translationsWithGroups = translationGroupData.flatMap((translationRegionGroup) =>
    translationRegionGroup.translations.map((translationData) => ({translationRegionGroup, translationData}))
  );
  const numTotal = translationsWithGroups.length;
  translationsWithGroups = translationsWithGroups.filter(({translationData}) =>
    isRangeOverlap(start, end, translationData.start, translationData.end)
  );
  const numVisible = translationsWithGroups.length;
  // On the linear and plasmid maps we limit how many features are shown, but for search we want to include all of them.
  if (!showAll) {
    translationsWithGroups = translationsWithGroups.slice(0, MAX_VISIBLE_RING_FEATURES);
  }
  const numShown = translationsWithGroups.length;
  const shownTranslations = translationsWithGroups.map(({translationRegionGroup, translationData}) => {
    // Ideally, we would only rely on a plain data object when rendering, but we need the actual
    // molbio/translation/backbone/Translation model in a few places to access some methods and to read and write
    // precomputed data. In the long run, we should try to move away from needing this reference.
    const translationComponentModel = translationComponentModelsByCid[translationData.cid];
    return new TranslationAsAnnotation({
      translationData,
      translationRegionGroup,
      translationComponentModel,
      sequenceStartIndex: sequence.startIndex(),
      sequenceLength: sequence.length(),
      // Layer information is pre-computed on Translations and necessary so that the translations can
      // be tiled properly.
      [LINEAR_LAYER_FULL]: translationComponentModel.get(LINEAR_LAYER_FULL),
      [LINEAR_LAYER_INLINE]: translationComponentModel.get(LINEAR_LAYER_INLINE),
      [LINEAR_LAYER_INLINE_PDF]: translationComponentModel.get(LINEAR_LAYER_INLINE_PDF),
    });
  });
  return {numTotal, numVisible, numShown, shownTranslations};
}

interface TranslationAsAnnotationAttributes {
  translationRegionGroup: TranslationRegionGroupData;
  translationData: TranslationRegionData;
  translationComponentModel: Translation;
  sequenceStartIndex: number;
  sequenceLength: number;
  // These values are pre-computed and represent the result of tiling translations to be non-overlapping.
  [LINEAR_LAYER_FULL]: number;
  [LINEAR_LAYER_INLINE]: number;
  [LINEAR_LAYER_INLINE_PDF]: number;
}

interface TranslationAsAnnotation {
  get<K extends keyof TranslationAsAnnotationAttributes>(key: K): TranslationAsAnnotationAttributes[K];
  // We currently expect the properties to be immutable, so disallow .set when using this interface.
  set: never;
}

/**
 * Backbone model intended for use with existing annotation rendering code.
 *
 * Ideally, new display and interaction logic would work off of pure data objects, but in the
 * meantime, this class acts as a bridge between translation data objects (including translation
 * group info) and the underlying molbio/translation/backbone/Translation model.
 */
class TranslationAsAnnotation
  extends RegionSelectable<Sequence>
  implements PolymerAnnotationsViewData<Sequence>, SegmentsRingData
{
  uid(): string {
    return this.get('translationData').cid;
  }

  start(): number {
    return this.get('translationData').start;
  }

  end(): number {
    return this.get('translationData').end;
  }

  hover(): boolean {
    return false;
  }

  color(): string {
    return this.get('translationRegionGroup').color;
  }

  strand(): Strand {
    return this.get('translationData').strand;
  }

  name(): string {
    return getTranslationRegionFormattedName(
      this.get('translationRegionGroup'),
      this.get('translationData').id,
      this.get('sequenceStartIndex'),
      this.get('sequenceLength')
    );
  }

  displayLabel(): string {
    return this.name();
  }

  visible(): boolean {
    return true;
  }

  /**
   * Used by LabelsView to distinguish from other feature types.
   */
  type(): string {
    return 'translation';
  }

  click(sequence: Sequence, shiftKey: boolean = false): void {
    const selection = sequence.get('selection');
    if (shiftKey) {
      selection.expandRange(this.start(), this.end());
    } else {
      selection.select(this.get('translationComponentModel'));
    }
  }

  dblClick(sequence: Sequence, shiftKey: boolean = false): void {
    const translationModel = this.get('translationComponentModel');
    if (sequence.allowFeatureEdits() && translationModel.editable()) {
      analytics.track('translation', 'open_translation_panel', {
        product: ANALYTICS_MODULE.MOLBIO,
        openedFrom: 'translation_double_click',
        sequence_id: sequence.get('id'),
      });
      sequence.get('selection').trigger('translation-panel', null, translationModel);
    }
  }
}

export default TranslationAsAnnotation;
